import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { Button, Dropdown, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MoreCircle } from 'iconsax-react'
import {
  FinanceInvoiceListResponseItem,
  InvoiceFinanceSortFieldEnum,
  OrderDirectionEnum,
} from 'generated/api'
import { IconCaretDown } from 'components/icons'
import IconSortAscDesc from 'components/tableHelpers/IconSortAscDesc'
import { mapStatusInvoice } from 'helpers/wording'
import { rupiahFormat } from 'helpers/formatter'

export interface PropsInvoiceTable {
  data: FinanceInvoiceListResponseItem[]
  orderDirection: OrderDirectionEnum
  orderColumn: string
  setSortingOrder: (columnKey: InvoiceFinanceSortFieldEnum) => void
}

function InvoiceTable(props: PropsInvoiceTable) {
  const navigate = useNavigate()

  const columnsInvoice: ColumnsType<FinanceInvoiceListResponseItem> = [
    {
      title: (
        <div>
          <Typography.Text className="font-semibold text-gray-800">No.</Typography.Text>
        </div>
      ),
      width: '5%',
      dataIndex: 'nomor',
      key: 'nomor',
      render: (_text, _row, index) => (
        <div>
          <Typography.Text className="font-normal text-sm text-[#323539]">
            {index + 1}.
          </Typography.Text>
        </div>
      ),
    },
    /**
     * Column name: Nama Pemesan | customerName
     * Column description: will render name of order
     */
    {
      title: (
        <div
          data-testid="btn_sort_customerName"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="font-semibold">Nama Pemesan</Typography.Text>
          <IconSortAscDesc
            columnName="customerName"
            columnSortBy={props.orderDirection}
            columnSortField={props.orderColumn}
          />
        </div>
      ),
      width: '15%',
      dataIndex: 'customerName',
      key: 'customerName',
      onHeaderCell: () => {
        return {
          onClick: () => props.setSortingOrder('customerName'),
        }
      },
      render: text => (
        <div data-testid="link_column_customerName" className="font-normal text-sm text-[#323539]">
          <Typography.Text>{text || '-'}</Typography.Text>
        </div>
      ),
      onCell: record => {
        return {
          onClick: () => {
            navigate(`/invoices/${record.id}`)
          },
        }
      },
    },
    /**
     * Column name: Nomor Invoice | invoiceNo
     * Column description: will return invoice number
     */
    {
      title: (
        <div
          data-testid="btn_sort_invoiceNo"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="font-semibold">Nomor Invoice</Typography.Text>
          <IconSortAscDesc
            columnName="invoiceNo"
            columnSortBy={props.orderDirection}
            columnSortField={props.orderColumn}
          />
        </div>
      ),
      width: '15%',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      onHeaderCell: () => {
        return {
          onClick: () => props.setSortingOrder('invoiceNo'),
        }
      },
      render: (text: string) => (
        <Typography.Text
          data-testid="link_column_invoiceNo"
          className="font-normal text-xs text-[#323539]"
        >
          {text || '-'}
        </Typography.Text>
      ),
      onCell: record => {
        return {
          onClick: () => {
            navigate(`/invoices/${record.id}`)
          },
        }
      },
    },
    /**
     * Column name: Tanggal Pesanan | orderCreatedAt
     * Column description: will render order date
     */
    {
      title: (
        <div
          data-testid="btn_sort_orderCreatedAt"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="font-semibold">Tanggal Pesanan</Typography.Text>
          <IconSortAscDesc
            columnName="orderCreatedAt"
            columnSortBy={props.orderDirection}
            columnSortField={props.orderColumn}
          />
        </div>
      ),
      width: '15%',
      dataIndex: 'orderCreatedAt',
      key: 'orderCreatedAt',
      onHeaderCell: () => {
        return {
          onClick: () => props.setSortingOrder('orderCreatedAt'),
        }
      },
      render: date => (
        <Typography.Text
          data-testid="link_column_orderCreatedAt"
          className="font-normal text-sm text-[#323539]"
        >
          {date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'}
        </Typography.Text>
      ),
      onCell: record => {
        return {
          onClick: () => {
            navigate(`/invoices/${record.id}`)
          },
        }
      },
    },
    /**
     * Column name: Tanggal Pembayaran | paidAr
     * Column description: will render paid dat
     */
    {
      title: (
        <div
          data-testid="btn_sort_paidAt"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="font-semibold">Tanggal Pembayaran</Typography.Text>
          <IconSortAscDesc
            columnName="paidAt"
            columnSortBy={props.orderDirection}
            columnSortField={props.orderColumn}
          />
        </div>
      ),
      width: '15%',
      dataIndex: 'paidAt',
      key: 'paidAt',
      onHeaderCell: () => {
        return {
          onClick: () => props.setSortingOrder('paidAt'),
        }
      },
      render: date => (
        <Typography.Text
          data-testid="link_column_paidAt"
          className="font-normal text-sm text-[#323539]"
        >
          {date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'}
        </Typography.Text>
      ),
      onCell: record => {
        return {
          onClick: () => {
            navigate(`/invoices/${record.id}`)
          },
        }
      },
    },
    /**
     * Column name: Total Tagihan | amountDue
     * Column description: will render amount due
     */
    {
      title: (
        <div
          data-testid="btn_sort_amountDue"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="font-semibold">Total Tagihan</Typography.Text>
          <IconSortAscDesc
            columnName="amountDue"
            columnSortBy={props.orderDirection}
            columnSortField={props.orderColumn}
          />
        </div>
      ),
      width: '15%',
      dataIndex: 'amountDue',
      key: 'amountDue',
      onHeaderCell: () => {
        return {
          onClick: () => props.setSortingOrder('amountDue'),
        }
      },
      render: amount => (
        <Typography.Text
          data-testid="link_column_amountDue"
          className="font-normal text-sm text-[#323539]"
        >
          {amount ? rupiahFormat(amount) : '-'}
        </Typography.Text>
      ),
      onCell: record => {
        return {
          onClick: () => {
            navigate(`/invoices/${record.id}`)
          },
        }
      },
    },
    /**
     * Column name: Status | status
     * Column description: will render status invoice
     */
    {
      title: (
        <div data-testid="btn_sort_status">
          <Typography.Text className="font-semibold">Status</Typography.Text>
        </div>
      ),
      width: '15%',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <Typography.Text
          data-testid="link_column_status"
          className="font-normal text-sm text-[#323539]"
        >
          {mapStatusInvoice(status)}
        </Typography.Text>
      ),
      onCell: record => {
        return {
          onClick: () => {
            navigate(`/invoices/${record.id}`)
          },
        }
      },
    },
    /**
     * Column name: Aksi | action
     * Column description: it will direct to invoice detail
     */
    {
      title: (
        <div className="flex justify-center">
          <MoreCircle size="16" color="#323539" />
        </div>
      ),
      width: '5%',
      key: 'action',
      render: (text, row, index) => (
        <Dropdown
          placement="bottomRight"
          trigger={['click']}
          dropdownRender={() => (
            <div className="flex flex-col gap-3 p-4 bg-white rounded-md shadow-xl">
              <span className="px-3 pt-2 font-semibold">Aksi</span>
              <span
                onClick={() => navigate('/invoices/' + props.data[index].id)}
                className="px-3 py-1 bg-white rounded-lg cursor-pointer hover:bg-primary hover:text-white"
              >
                Lihat Detail
              </span>
            </div>
          )}
          arrow
        >
          <Button
            data-testid={`select_table_invoice_action_dropdown_${index}`}
            block
            className="bg-transparent"
          >
            <Typography.Text className="font-medium cursor-pointer">
              <IconCaretDown />
            </Typography.Text>
          </Button>
        </Dropdown>
      ),
    },
  ]

  return (
    <Table
      rowKey="id"
      data-testid="invoice_table"
      className="table-primary-admin"
      columns={columnsInvoice}
      dataSource={props.data}
      pagination={false}
    />
  )
}

export default InvoiceTable
